import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { hitApi, dateFormatter, commonToast } from "../../components/commonAction";
import AccountTabs from "./AccountTabs";

export default function MySubscriptions({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancellingId, setCancellingId] = useState(null);

    const load = () => {
        setLoading(true);
        hitApi({ url: "billing/me/subscriptions", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setRows(resp.data?.subscriptions || []);
        });
    };
    useEffect(load, []);

    const cancel = (sub) => {
        if (!window.confirm("Cancel this subscription? Access remains until the end of the current billing period.")) return;
        setCancellingId(sub._id);
        hitApi(
            { url: `billing/me/subscriptions/${sub._id}/cancel`, method: "POST", data: {}, alert: false },
            (resp) => {
                setCancellingId(null);
                if (resp?.status) { commonToast("success", resp.message); load(); }
            }
        );
    };

    const active    = rows.filter(r => r.status === "active").length;
    const cancelled = rows.filter(r => r.status === "cancelled").length;

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap">
                <div className="pxl-headingTop">
                    <h2 className="pxl-rb-header-heading">My Billing</h2>
                </div>
            </div>
            <AccountTabs />

            {/* Stats row */}
            {!loading && rows.length > 0 && (
                <div className="pxl-bill-stats">
                    <div className="pxl-bill-stat pxl-bill-stat--accent">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Total</span>
                            <span className="pxl-bill-stat-icon"><IconRepeat /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{rows.length}</div>
                    </div>
                    <div className="pxl-bill-stat pxl-bill-stat--success">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Active</span>
                            <span className="pxl-bill-stat-icon"><IconCheck /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{active}</div>
                    </div>
                    <div className="pxl-bill-stat pxl-bill-stat--warning">
                        <div className="pxl-bill-stat-head">
                            <span className="pxl-bill-stat-label">Cancelled</span>
                            <span className="pxl-bill-stat-icon"><IconX /></span>
                        </div>
                        <div className="pxl-bill-stat-value">{cancelled}</div>
                    </div>
                </div>
            )}

            {/* Panel */}
            <div className="pxl-bill-panel">
                <div className="pxl-bill-panel-header">
                    <div>
                        <h3 className="pxl-bill-panel-title">Subscriptions</h3>
                        <div className="pxl-bill-panel-sub">Active subscriptions renew automatically each billing period.</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span className="pxl-bill-panel-count">{rows.length}</span>
                        <button className="pxl-bill-refresh-btn" onClick={load}><IconRefresh /> Refresh</button>
                    </div>
                </div>

                <div className="pxl-bill-table-wrap">
                    <table className="pxl-bill-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Started</th>
                                <th>Period ends</th>
                                <th>PayPal ID</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    {[0, 1, 2, 3, 4].map(i => (
                                        <tr key={i}>
                                            <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '60%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '65%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '70%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: 64, height: 30, borderRadius: 6, display: 'block' }} /></td>
                                        </tr>
                                    ))}
                                </>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan="6" className="pxl-bill-table-state-cell">
                                    <EmptyState
                                        icon={<IconRepeat />}
                                        title="No subscriptions yet"
                                        text="Your recurring subscriptions will appear here. Pick a monthly plan and billing is handled automatically."
                                        cta={<Link to="/pricing" className="pxl-bill-empty-cta"><IconArrow /> Browse plans</Link>}
                                    />
                                </td></tr>
                            ) : rows.map(r => (
                                <tr key={r._id}>
                                    <td><span className="pxl-bill-cell-strong">{r.type}</span></td>
                                    <td>
                                        {r.status === "active"
                                            ? <span className="pxl-bill-pill pxl-bill-pill--success"><IconDot /> Active</span>
                                            : r.status === "cancelled"
                                                ? <span className="pxl-bill-pill pxl-bill-pill--warning">Cancelled</span>
                                                : <span className="pxl-bill-pill pxl-bill-pill--muted">{r.status}</span>}
                                    </td>
                                    <td><span className="pxl-bill-cell-date">{r.startedAt ? dateFormatter(r.startedAt, "D MMM YYYY") : "—"}</span></td>
                                    <td><span className="pxl-bill-cell-date">{r.currentPeriodEnd ? dateFormatter(r.currentPeriodEnd, "D MMM YYYY") : "—"}</span></td>
                                    <td><span className="pxl-bill-cell-mono">{r.paypalSubscriptionId || "—"}</span></td>
                                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                        {r.status === "active" && (
                                            <button
                                                className="pxl-bill-cancel-btn"
                                                disabled={cancellingId === r._id}
                                                onClick={() => cancel(r)}
                                            >
                                                {cancellingId === r._id ? "Cancelling…" : "Cancel"}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ icon, title, text, cta }) {
    return (
        <div className="pxl-bill-empty">
            <div className="pxl-bill-empty-icon">{icon}</div>
            <div className="pxl-bill-empty-title">{title}</div>
            <p className="pxl-bill-empty-text">{text}</p>
            {cta}
        </div>
    );
}

/* ── Icons ── */
function IconRepeat()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>; }
function IconCheck()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function IconX()       { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function IconRefresh() { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>; }
function IconArrow()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>; }
function IconDot()     { return <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>; }
